import { Injectable, NgZone, inject, signal } from '@angular/core';
// import { DataFactory } from '../../services/DataFactory';
import { DataAggregationService } from '../../../core/services/data-aggregation.service';
import { ProcessedSubmissionsService } from './processed-submissions.service';
import { environment } from '../../../../environments/environment.development';
import {AMS , UWI} from './submission.types'


const FOUR_STEP_APPLICATION_TYPES = new Set(['485', '495', '499']);
const FIVE_STEP_APPLICATION_TYPES = new Set(['492']);
const FOUR_STEP_PROCESS_EVENTS = [
  'submission_process_started',
  'indexing_completed',
  'submission_details_processed',
  'completed'
] as const;
const FIVE_STEP_PROCESS_EVENTS = [
  'submission_process_started',
  'indexing_completed',
  'submission_details_processed',
  'submission_insights_processed',
  'completed'
] as const;

@Injectable({ providedIn: 'root' })
export class SubmissionProcessingStateService {
  private readonly dataFactory = inject(DataAggregationService);
  private readonly ngZone = inject(NgZone);
  private readonly processedSubmissions = inject(ProcessedSubmissionsService);

  readonly processingSubmissionIds = signal<Record<string, boolean>>({});
  readonly processingProgressBySubmissionId = signal<Record<string, number>>({});
  readonly submissionActionStatusOverrides = signal<Record<string, UWI.SubmissionActionStatus>>({});
  readonly transientSocketStatus = signal<UWI.SubmissionLiveStatusMessage | null>(null);

  private readonly submissionProcessSockets = new Map<string, WebSocket>();
  private transientSocketStatusTimeout?: ReturnType<typeof setTimeout>;

  getRowActionStatus(row: AMS.SubmissionRow): UWI.SubmissionActionStatus | null {
    return this.submissionActionStatusOverrides()[row.id] ?? row.actionStatus;
  }

  startSubmissionProcessing(row: AMS.SubmissionRow, userId: number): void {
    if (this.processingSubmissionIds()[row.id] === true) {
      return;
    }

    this.setSubmissionProcessingState(row.id, true);
    this.updateSubmissionProcessingProgress(row.id, 0);
    this.connectSubmissionProcessSocket(row, userId);
  }

  private connectSubmissionProcessSocket(row: AMS.SubmissionRow, userId: number): void {
    this.closeSubmissionProcessSocket(row.id);

    const socket = new WebSocket(this.buildSubmissionProcessSocketUrl(userId, row.id));

    socket.onopen = () => {
      this.dataFactory.processSubmission({
        user_id: userId,
        quote_id: row.quoteId,
        submission_id: row.id,
        application_type_id: row.applicationType
      }).subscribe({
        next: (response) => {
          const status = this.readRecordString(response, 'status').toLowerCase();

          if (status === 'success') {
            this.submissionActionStatusOverrides.update((state) => ({
              ...state,
              [row.id]: {
                isProcessed: true,
                hasDocument: true
              }
            }));
            this.processedSubmissions.markSubmissionProcessed(row.id);
            this.updateSubmissionProcessingProgress(row.id, 100);
            this.setSubmissionProcessingState(row.id, false);
            this.closeSubmissionProcessSocket(row.id);
            return;
          }

          if (status !== 'accepted' && status !== 'success') {
            this.setSubmissionProcessingState(row.id, false);
            this.updateSubmissionProcessingProgress(row.id, 0);
            this.closeSubmissionProcessSocket(row.id);
          }
        },
        error: () => {
          this.setSubmissionProcessingState(row.id, false);
          this.updateSubmissionProcessingProgress(row.id, 0);
          this.closeSubmissionProcessSocket(row.id);
        }
      });
    };

    socket.onmessage = (event) => {
      this.ngZone.run(() => {
        this.handleSubmissionProcessSocketMessage(row, event.data);
      });
    };

    socket.onerror = () => {
      this.ngZone.run(() => {
        this.setSubmissionProcessingState(row.id, false);
        this.closeSubmissionProcessSocket(row.id);
      });
    };

    socket.onclose = () => {
      this.submissionProcessSockets.delete(row.id);

      if (this.processingSubmissionIds()[row.id] === true) {
        this.setSubmissionProcessingState(row.id, false);
      }
    };

    this.submissionProcessSockets.set(row.id, socket);
  }

  private handleSubmissionProcessSocketMessage(row: AMS.SubmissionRow, raw: unknown): void {
    if (typeof raw !== 'string') {
      return;
    }

    let payload: UWI.SubmissionProcessSocketEvent;

    try {
      payload = JSON.parse(raw) as UWI.SubmissionProcessSocketEvent;
    } catch {
      return;
    }

    const submissionId = String(payload.submission_id ?? '');

    if (!submissionId || submissionId !== row.id) {
      return;
    }

    const normalizedEvent = this.normalizeProcessEvent(payload.event);
    this.showTransientSocketStatus(row, payload);

    if (!normalizedEvent) {
      return;
    }

    const applicationTypeId = String(payload.application_type_id ?? row.applicationType);
    const eventSequence = this.getProcessEventSequence(applicationTypeId);
    const eventIndex = eventSequence.indexOf(normalizedEvent);

    if (eventIndex === -1) {
      return;
    }

    const progress = Math.round(((eventIndex + 1) / eventSequence.length) * 100);
    this.updateSubmissionProcessingProgress(row.id, progress);

    if (normalizedEvent === 'completed') {
      this.submissionActionStatusOverrides.update((state) => ({
        ...state,
        [row.id]: {
          isProcessed: true,
          hasDocument: true
        }
      }));
      this.processedSubmissions.markSubmissionProcessed(row.id);
      this.setSubmissionProcessingState(row.id, false);
      this.closeSubmissionProcessSocket(row.id);
    }
  }

  private getProcessEventSequence(applicationTypeId: string): readonly string[] {
    if (FIVE_STEP_APPLICATION_TYPES.has(applicationTypeId)) {
      return FIVE_STEP_PROCESS_EVENTS;
    }

    if (FOUR_STEP_APPLICATION_TYPES.has(applicationTypeId)) {
      return FOUR_STEP_PROCESS_EVENTS;
    }

    return FOUR_STEP_PROCESS_EVENTS;
  }

  private normalizeProcessEvent(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim().toLowerCase();
  }

  private buildSubmissionProcessSocketUrl(userId: number, submissionId: string): string {
    return environment.submissionInsightWebSocketUrl
      .replace('{userId}', String(userId))
      .replace('{submissionId}', submissionId);
  }

  private showTransientSocketStatus(row: AMS.SubmissionRow, payload: UWI.SubmissionProcessSocketEvent): void {
    const eventMessage = typeof payload.message === 'string' ? payload.message.trim() : '';
    const fallbackMessage = this.formatSocketEventLabel(payload.event);
    const message = eventMessage || fallbackMessage;

    if (!message) {
      return;
    }

    const underwriterName = row.underwriter?.trim() || 'Underwriter';
    const quoteId = row.quoteId?.trim() || 'N/A';

    this.transientSocketStatus.set({
      title: `Hi ${underwriterName} | Submission ID: ${row.id} | Quote ID: ${quoteId}`,
      detail: message
    });

    if (this.transientSocketStatusTimeout) {
      clearTimeout(this.transientSocketStatusTimeout);
    }

    this.transientSocketStatusTimeout = setTimeout(() => {
      this.transientSocketStatus.set(null);
      this.transientSocketStatusTimeout = undefined;
    }, 3000);
  }

  private updateSubmissionProcessingProgress(submissionId: string, progress: number): void {
    this.processingProgressBySubmissionId.update((state) => {
      const current = state[submissionId] ?? 0;

      return {
        ...state,
        [submissionId]: Math.max(current, progress)
      };
    });
  }

  private setSubmissionProcessingState(submissionId: string, isProcessing: boolean): void {
    this.processingSubmissionIds.update((state) => ({
      ...state,
      [submissionId]: isProcessing
    }));
  }

  private closeSubmissionProcessSocket(submissionId: string): void {
    const socket = this.submissionProcessSockets.get(submissionId);

    if (!socket) {
      return;
    }

    socket.close();
    this.submissionProcessSockets.delete(submissionId);
  }

  private readRecordString(value: unknown, key: string): string {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return '';
    }

    const record = value as Record<string, unknown>;
    const entry = record[key];

    if (typeof entry === 'string') {
      return entry.trim();
    }

    if (typeof entry === 'number' && Number.isFinite(entry)) {
      return String(entry);
    }

    return '';
  }

  private formatSocketEventLabel(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
